// ======================================================================
// 📁 File: src/modules/ticket/forms/CreateTicketForm.js
// 🎨 FULL MULTILINGUAL VERSION (CUSTOM FIELDS INCLUDED)
// ======================================================================

import React, { useState, useEffect } from "react";
import axiosClient from "../../../api/axiosClient";
import CustomFieldInput from "./CustomFieldInput";
import { useTranslation } from "react-i18next";
import "./TicketForm.css";

export default function CreateTicketForm() {
  const { t } = useTranslation();

  // --------------------------------------------------------
  // MAIN FORM STATE
  // --------------------------------------------------------
  const [form, setForm] = useState({
    title: "",
    description: "",
    ticket_type_id: "",
    category_id: "",
    severity: "medium",
    priority: "medium",
  });

  const [customValues, setCustomValues] = useState({});

  const [ticketTypes, setTicketTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [customFields, setCustomFields] = useState([]);

  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);

  // --------------------------------------------------------
  // LOAD LOOKUPS
  // --------------------------------------------------------
  useEffect(() => {
    loadLookups();
  }, []);

  const extractLookupPayload = (raw) => {
    const root = raw?.data ? raw.data : raw || {};
    const tr = root.translations || null;
    return {
      ticket_types: (tr || root).ticket_types || [],
      categories: (tr || root).categories || [],
      custom_fields: (tr || root).custom_fields || [],
    };
  };

  const loadLookups = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await axiosClient.get("/ticket-management/create-support-data");

      const { ticket_types, categories, custom_fields } =
        extractLookupPayload(res.data || res);

      setTicketTypes(ticket_types);
      setCategories(categories);
      setCustomFields(custom_fields);

      // Init custom fields
      const initial = {};
      custom_fields.forEach((f) => {
        const id = f.id ?? f.field_id;
        const tp = f.field_type || f.type;
        initial[id] = tp === "checkbox" ? false : "";
      });

      setCustomValues(initial);
      setLoading(false);
    } catch (err) {
      console.error("Lookup load error:", err);
      setLoadError(err);
      setLoading(false);
    }
  };

  // --------------------------------------------------------
  // HANDLERS
  // --------------------------------------------------------
  const handleChange = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleCustomFieldChange = (fieldId, value) => {
    setCustomValues((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const buildPayload = () => ({
    ...form,
    custom_fields: customValues,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = buildPayload();
    console.log("Submitting Ticket Payload:", payload);

    try {
      await axiosClient.post("/ticket-management/tickets", payload);

      alert(t("ticket.create.success"));

      // Reset the form
      setForm({
        title: "",
        description: "",
        ticket_type_id: "",
        category_id: "",
        severity: "medium",
        priority: "medium",
      });

      const resetCF = {};
      customFields.forEach((f) => {
        const id = f.id ?? f.field_id;
        const tp = f.field_type || f.type;
        resetCF[id] = tp === "checkbox" ? false : "";
      });
      setCustomValues(resetCF);
    } catch (err) {
      console.error("Create ticket error:", err);
      alert(t("ticket.create.failed"));
    }
  };

  const optionLabel = (item) => item.name || item.label || String(item);

  // ======================================================================
  // RENDER
  // ======================================================================

  return (
    <div className="ticket-page-wrapper">
      {/* Banner */}
      <div className="ticket-banner">
        <h3 className="title">
          <span className="icon">🎫</span> {t("ticket.create.header")}
        </h3>
        <p className="subtitle">{t("ticket.create.subtitle")}</p>
      </div>

      <div className="ticket-wide-container">
        <form onSubmit={handleSubmit}>
          
          {/* GENERAL INFORMATION */}
          <div className="ticket-card">
            <div className="ticket-card-header">
              {t("ticket.section.general")}
            </div>

            <div className="ticket-card-body row g-4">

              <div className="col-md-6 floating-label-group">
                <input
                  className="form-control"
                  type="text"
                  name="title"
                  placeholder=" "
                  value={form.title}
                  onChange={handleChange}
                  required
                />
                <label className="floating-label">
                  {t("ticket.form.title")}
                </label>
              </div>

              <div className="col-md-6 floating-label-group">
                <textarea
                  className="form-control"
                  name="description"
                  placeholder=" "
                  rows="3"
                  value={form.description}
                  onChange={handleChange}
                />
                <label className="floating-label">
                  {t("ticket.form.description")}
                </label>
              </div>

            </div>
          </div>

          {/* CLASSIFICATION */}
          <div className="ticket-card">
            <div className="ticket-card-header">
              {t("ticket.section.classification")}
            </div>

            <div className="ticket-card-body row g-4">

              {/* TICKET TYPE */}
              <div className="col-md-3 floating-label-group">
                <select
                  className="form-select"
                  name="ticket_type_id"
                  value={form.ticket_type_id}
                  onChange={handleChange}
                >
                  <option value="">{t("ticket.form.select_ticket_type")}</option>
                  {ticketTypes.map((tType) => (
                    <option key={tType.id} value={tType.id}>
                      {optionLabel(tType)}
                    </option>
                  ))}
                </select>
                <label className="floating-label">
                  {t("ticket.form.ticket_type")}
                </label>
              </div>

              {/* CATEGORY */}
              <div className="col-md-3 floating-label-group">
                <select
                  className="form-select"
                  name="category_id"
                  value={form.category_id}
                  onChange={handleChange}
                >
                  <option value="">{t("ticket.form.select_category")}</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {optionLabel(c)}
                    </option>
                  ))}
                </select>
                <label className="floating-label">
                  {t("ticket.form.category")}
                </label>
              </div>

              {/* SEVERITY */}
              <div className="col-md-3 floating-label-group">
                <select
                  className="form-select"
                  name="severity"
                  value={form.severity}
                  onChange={handleChange}
                >
                  <option value="low">{t("ticket.severity.low")}</option>
                  <option value="medium">{t("ticket.severity.medium")}</option>
                  <option value="high">{t("ticket.severity.high")}</option>
                  <option value="critical">{t("ticket.severity.critical")}</option>
                </select>
                <label className="floating-label">
                  {t("ticket.form.severity")}
                </label>
              </div>

              {/* PRIORITY */}
              <div className="col-md-3 floating-label-group">
                <select
                  className="form-select"
                  name="priority"
                  value={form.priority}
                  onChange={handleChange}
                >
                  <option value="low">{t("ticket.priority.low")}</option>
                  <option value="medium">{t("ticket.priority.medium")}</option>
                  <option value="high">{t("ticket.priority.high")}</option>
                </select>
                <label className="floating-label">
                  {t("ticket.form.priority")}
                </label>
              </div>

            </div>
          </div>

          {/* CUSTOM FIELDS */}
          <div className="ticket-card">
            <div className="ticket-card-header">
              {t("ticket.section.custom_fields")}
            </div>

            <div className="ticket-card-body row g-4">

              {loading && <div>{t("ticket.loading")}</div>}

              {!loading && customFields.length === 0 && (
                <div className="text-muted">
                  {t("ticket.no_custom_fields")}
                </div>
              )}

              {!loading &&
                customFields.map((field) => {
                  const id = field.id ?? field.field_id;

                  // NATURAL MULTILINGUAL SUPPORT FOR CUSTOM FIELD LABEL
                  const labelKey = `ticket.custom.${field.name}`;
                  const translatedLabel = t(labelKey);

                  return (
                    <div className="col-md-6" key={id}>
                      <CustomFieldInput
                        field={{
                          ...field,
                          label: translatedLabel !== labelKey
                            ? translatedLabel
                            : field.label, // fallback to DB label
                        }}
                        value={customValues[id]}
                        onChange={(fieldId, val) =>
                          handleCustomFieldChange(fieldId, val)
                        }
                      />
                    </div>
                  );
                })}

              {loadError && (
                <div className="text-danger mt-3">
                  {t("ticket.custom_fields_load_failed")}
                </div>
              )}

            </div>
          </div>

          {/* ATTACHMENTS */}
          <div className="ticket-card">
            <div className="ticket-card-header">
              {t("ticket.section.attachments")}
            </div>
            <div className="ticket-card-body">
              <input className="form-control" type="file" multiple />
            </div>
          </div>

          {/* SUBMIT BUTTON */}
          <div className="ticket-submit-bar">
            <button className="ticket-submit-btn" type="submit">
              {t("ticket.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
